import React, { useEffect, useState } from "react";
import { db } from "./firebase";
import { ref, onValue } from "firebase/database";
import { jwtDecode } from "jwt-decode";
import { decryptStringBase64 } from "./cryptohelpers";
import { logoutApi } from "./bankApi/logout";
import { useDispatch, useSelector } from "react-redux";
import { signout } from "../feature/authSlice";
import { setPermissions } from "../feature/permissions";
import { toast, ToastContainer } from "react-toastify";
import * as CryptoJS from "crypto-js";
import { EncKeys } from "../service/config";
import "react-toastify/dist/ReactToastify.css";

const NotificationListener = () => {
  const { authorized, user } = useSelector((state) => state.auth);
  const dispatch = useDispatch();
  const [currentUserId, setCurrentUserId] = useState(null);
  const [currentSecurityStamp, setCurrentSecurityStamp] = useState(null);

  // Decrypt and extract user data ONCE when component mounts
  useEffect(() => {
    if (!authorized) {
      // console.log("⏸️ User not authorized, skipping session check");
      return;
    }

    try {
      const stored = localStorage.getItem("access_token");
      if (!stored) {
        console.error("❌ No access_token in localStorage");
        return;
      }

      // Decrypt token
      const decrypted = decryptStringBase64(stored);
      if (!decrypted) {
        console.error("❌ Decryption failed");
        return;
      }

      // Extract JWT from JSON wrapper if needed
      let accessTokenPlain = decrypted;
      if (accessTokenPlain.startsWith("{")) {
        try {
          const parsed = JSON.parse(accessTokenPlain);
          accessTokenPlain = parsed.token || accessTokenPlain;
        } catch (e) {
          console.error("❌ Failed to parse JSON token wrapper");
        }
      }

      // Validate JWT format
      if (accessTokenPlain.split(".").length !== 3) {
        console.error("❌ Invalid JWT format");
        return;
      }

      // Decode JWT
      const decodedToken = jwtDecode(accessTokenPlain);
      const userId =
        decodedToken[
          "http://schemas.xmlsoap.org/ws/2005/05/identity/claims/sid"
        ];

      // Decrypt security stamp
      const encKey = CryptoJS.enc.Utf8.parse(EncKeys.VayanaEncKey);
      const iv = CryptoJS.enc.Utf8.parse(EncKeys.IV);
      const encryptedStamp = localStorage.getItem("security_stamp");

      if (!encryptedStamp) {
        console.error("❌ No security_stamp in localStorage");
        return;
      }

      const decryptedSecurityStamp = CryptoJS.AES.decrypt(
        { ciphertext: CryptoJS.enc.Base64.parse(encryptedStamp) },
        encKey,
        {
          keySize: 128 / 8,
          iv,
          mode: CryptoJS.mode.CBC,
          padding: CryptoJS.pad.Pkcs7,
        }
      ).toString(CryptoJS.enc.Utf8);

      // console.log("✅ User authenticated:", userId);
      setCurrentUserId(userId);
      setCurrentSecurityStamp(decryptedSecurityStamp);
    } catch (error) {
      console.error("❌ Error extracting user data:", error);
    }
  }, [authorized]);

  // Firebase listener - only setup when we have userId
  useEffect(() => {
    if (!currentUserId || !currentSecurityStamp) {
      // console.log("⏸️ Waiting for user data...");
      return;
    }

    // console.log("🔵 Setting up Firebase listener for user:", currentUserId);

    // Listen to this specific user's session
    const notificationsRef = ref(
      db,
      `ConcurrentSessionCheck/${currentUserId}`
    );

    const unsubscribe = onValue(notificationsRef, (snapshot) => {
      const data = snapshot.val();

      if (!data) {
        // console.log("📡 No session data yet");
        return;
      }

      // console.log("📡 Session data received:", data);

      // Get the security stamp from Firebase
      const firebaseSecurityStamp = data.SecurityStamp;

      // console.log("🔍 Comparing stamps:");
      // console.log("  Local:", currentSecurityStamp);
      // console.log("  Firebase:", firebaseSecurityStamp);

      // If stamps don't match, another session logged in
      if (
        firebaseSecurityStamp &&
        currentSecurityStamp !== firebaseSecurityStamp
      ) {
        console.warn("⚠️ Concurrent login detected - logging out");

        if (!window.__alreadyLoggedOut) {
          window.__alreadyLoggedOut = true;

          toast.error("Your account was logged in from another device.", {
            position: "top-center",
            autoClose: 5000,
          });

          setTimeout(() => {
            dispatch(
              logoutApi(
                {},
                () => {
                  dispatch(signout());
                  dispatch(setPermissions(null));
                  localStorage.clear();
                  window.location.href = "/login";
                },
                () => {
                  // Fallback if API fails
                  dispatch(signout());
                  dispatch(setPermissions(null));
                  localStorage.clear();
                  window.location.href = "/login";
                }
              )
            );
          }, 2000);
        }
      } else {
       //  console.log("✅ Security stamps match - session valid");
      }
    });

    // Cleanup
    return () => {
     //  console.log("🧹 Cleaning up Firebase listener");
      unsubscribe();
    };
  }, [currentUserId, currentSecurityStamp, dispatch]);

  return <ToastContainer />;
};

export default NotificationListener;
